import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { PrintFrameService } from '../../services/print-frame.service';
import { environment } from '../../../environments/environment';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-print-frame',
  templateUrl: './print-frame.component.html'
})
export class PrintFrameComponent implements OnInit, OnDestroy {
  pageType = '';
  isLoading = true;

  // Static banner slides (hardcoded — no longer from API)
  slides: any[] = [
    { image: 'https://storage.googleapis.com/static-mahattaart-online/banner/SM_P2F-Banner_07%20(1).webp', mobile_image: 'https://storage.googleapis.com/static-mahattaart-online/banner/Phone-Banner-01b.webp', button_link: '/print-frame/featured-frames', alt_text: 'Print & Frame Banner' },
    // Add more banners here to enable auto-slide. Example:
    // { image: 'https://storage.googleapis.com/static-mahattaart-online/banner/SM_P2F-Banner_01.webp', button_link: '', alt_text: 'Print & Frame Banner' },
    // { image: 'https://storage.googleapis.com/static-mahattaart-online/banner/SM_P2F-Banner_03.webp', button_link: '', alt_text: 'Print & Frame Banner' },
  ];
  homeDecors: any[] = [];
  services: any[] = [];
  settingsFrames: any[] = [];
  headings: any[] = [];

  // Featured frames (on_sale) from API
  featuredFrames: any[] = [];
  newArrivals: any[] = [];
  featuredTotal = 0;

  // Carousel
  currentSlide = 0;
  private slideInterval: any;

  // Contact form
  contactForm = { name: '', email: '', contact: '', requirement: '' };
  formSubmitting = false;
  formSuccess = false;
  formError = false;

  private sub = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private pfService: PrintFrameService,
    private http: HttpClient,
    private dataLayer: DataLayerService,
  ) {}

  ngOnInit() {
    this.sub.add(this.route.data.subscribe(data => {
      this.pageType = data['page'] || 'photo-frame';
      this.isLoading = true;
      this.loadData();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.stopSlideshow();
  }

  private loadData() {
    // Slides are now hardcoded (see `slides` property) — kick off auto-slide if more than one
    if (this.slides.length > 1) this.startSlideshow();

    // Load remaining settings (services, frames, home_decors) from API
    this.pfService.getSettings().subscribe({
      next: (res: any) => {
        const fp = res?.data?.frame_photos || {};
        // Fix broken S3 URLs → replace with production CDN
        const fixUrl = (url: string) => url ? url.replace('dev-mahattaart2021.s3.ap-south-1.amazonaws.com', 'content.mahattaart.com') : url;
        this.homeDecors = (fp.home_decors || [])
          .filter((s: any) => s.published === '1' || !s.published)
          .map((s: any) => ({ ...s, image: fixUrl(s.image), mobile_image: fixUrl(s.mobile_image) }))
          .slice(0, 4);
        this.services = fp.services || [];
        this.settingsFrames = fp.frames || [];
        this.headings = fp.heading || [];
      },
      error: () => {}
    });

    // Load featured frames
    this.pfService.getFeaturedFrames(0, 8).subscribe({
      next: (res: any) => {
        const items = res?.data || [];
        this.featuredFrames = items.slice(0, 4);
        this.newArrivals = items.slice(4, 8);
        this.featuredTotal = res?.meta?.recordsFiltered || items.length;
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  // Carousel
  startSlideshow() {
    this.slideInterval = setInterval(() => {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    }, 5000);
  }

  stopSlideshow() {
    if (this.slideInterval) clearInterval(this.slideInterval);
  }

  goToSlide(index: number) {
    this.currentSlide = index;
    this.stopSlideshow();
    this.startSlideshow();
  }

  // Frame helpers
  getFrameImage(item: any): string {
    return item.image || item.thumbnail || item.images?.[0] || item.on_sale_detail?.image || 'assets/images/product/on_sale/framed-art.jpg';
  }

  getFrameTitle(item: any): string {
    return item.title || item.name || '';
  }

  getFramePrice(item: any): number {
    return item.price || item.selling_price || item.mrp || 0;
  }

  getFrameMrp(item: any): number {
    return item.mrp || item.original_price || 0;
  }

  // Navigation
  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  navigateToFeatured(item: any) {
    const slug = item.title_slug || item.slug || item.id;
    this.router.navigate(['/featured-frame', slug]);
  }

  // Email validation
  get isEmailValid(): boolean {
    return /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/.test(this.contactForm.email);
  }

  // Contact form
  submitQuery() {
    if (!this.contactForm.name || !this.contactForm.email || !this.contactForm.contact || !this.contactForm.requirement) return;
    if (!this.isEmailValid) return;

    this.formSubmitting = true;
    this.formError = false;

    const payload = {
      name: this.contactForm.name,
      email: this.contactForm.email,
      contact: this.contactForm.contact,
      requirement: this.contactForm.requirement,
      subject: 'Contact us Query',
      page_url: this.router.url
    };

    this.http.post(`${environment.apiUrl}/api/unauth/contact`, payload).subscribe({
      next: () => {
        this.formSubmitting = false;
        this.formSuccess = true;
        this.contactForm = { name: '', email: '', contact: '', requirement: '' };
        setTimeout(() => this.formSuccess = false, 10000);
      },
      error: () => {
        this.formSubmitting = false;
        this.formError = true;
        setTimeout(() => this.formError = false, 10000);
      }
    });
  }
}
